import { Component, OnInit } from '@angular/core';
import {
  combineLatest,
  take,
  firstValueFrom,
  map,
  Observable,
  filter,
  EMPTY,
  BehaviorSubject,
  switchMap,
  tap,
  distinctUntilChanged,
} from 'rxjs';

import {
  NotificationPreferencesStateService,
  NotificationType,
  NotificationGroup,
} from '@usf/ngrx-alerts';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ViewWillLeave, ViewWillEnter } from '@ionic/angular';
import { CustomerStoreService } from '@app/ngrx-customer/services/customer/customer-store.service';
import { Customer } from '@usf/customer-types';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { PlatformService, PanAppState, UserState } from '@panamax/app-state';
import {
  SaveNotificationPreferencesRequest,
  AlertType,
} from '@usf/alert-types';
import { ToastService } from '@shared/services/toast/toast.service';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '@app/user/services/user.service';
import { PATHS } from '@shared/constants/paths';
import { PendingNotificationPreferencesService } from '../services/pending-notfication-preferences.service';

/**
 * View model interface representing the component's state
 */
interface TypeOverviewPageViewModel {
  notificationType: NotificationType;
  availableCustomers: Customer[];
  user: UserState;
  isInternalUser: boolean;
  uiState: UIState;
}

/**
 * Interface representing the UI-specific state managed by the component
 */
interface UIState {
  showToggleAllToast: boolean;
  toggleAllToastMessage: string;
  dontShowAgainAllOn: boolean;
  dontShowAgainAllOff: boolean;
  breadcrumbs: Crumb[];
}

@Component({
  selector: 'app-notification-type-overview',
  templateUrl: './notification-preferences-type-overview.page.html',
  styleUrls: ['./notification-preferences-type-overview.page.scss'],
  standalone: false,
})
export class NotificationPreferencesTypeOverviewPage
  implements OnInit, ViewWillEnter, ViewWillLeave
{
  private static readonly TYPE_ID_PARAM = 'typeId';
  public viewModel$: Observable<TypeOverviewPageViewModel> = EMPTY;
  private readonly pageInViewSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  /** Subject managing all UI-related state */
  private readonly uiStateSubject = new BehaviorSubject<UIState>({
    showToggleAllToast: false,
    toggleAllToastMessage: '',
    dontShowAgainAllOn: false,
    dontShowAgainAllOff: false,
    breadcrumbs: [
      {
        label: 'i18n.notifications.preferences.backToDashboard',
        url: '/desktop/notifications/preferences',
        active: false,
      },
    ],
  });

  constructor(
    private readonly notificationPreferencesStateService: NotificationPreferencesStateService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly customerStoreService: CustomerStoreService,
    private readonly panAppState: PanAppState,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly userService: UserService,
    public readonly platformService: PlatformService,
    private readonly pendingNotificationPreferencesService: PendingNotificationPreferencesService,
  ) {}

  ngOnInit(): void {
    this.viewModel$ = this.pageInViewSubject.pipe(
      switchMap(inView => {
        if (!inView) return EMPTY;
        return combineLatest([
          this.notificationPreferencesStateService.selectActiveNotificationType$(),
          this.customerStoreService.loadCustomers$().pipe(
            filter(customers => customers.length > 0),
            take(1),
          ),
          this.panAppState.user$,
          this.panAppState.accessToken$.pipe(
            map(accessToken => this.userService.isInternalUser(accessToken)),
          ),
          this.uiStateSubject.pipe(distinctUntilChanged()),
        ]).pipe(
          tap(([activeNotificationType]) => {
            const pageTitle =
              this.translateService.instant(
                'i18n.notifications.preferences.ALERT_TYPE_' +
                  activeNotificationType?.type,
              ) +
              ' ' +
              this.translateService.instant('i18n.notifications.alerts');
            this.panAppState.setPageTitle(pageTitle);
          }),
          map(
            ([
              notificationType,
              availableCustomers,
              user,
              isInternalUser,
              uiState,
            ]) => ({
              notificationType,
              availableCustomers,
              user,
              isInternalUser,
              uiState,
            }),
          ),
        );
      }),
    );
  }

  /**
   * Lifecycle hook called when the page is about to enter
   * Initializes the page state and loads notification type details
   */
  ionViewWillEnter() {
    this.pageInViewSubject.next(true);

    // Get the notification type from route parameters and initialize state
    firstValueFrom(
      this.route.params.pipe(
        map<Params, string>(
          params =>
            params[NotificationPreferencesTypeOverviewPage.TYPE_ID_PARAM],
        ),
      ),
    ).then((alertType: string) => {
      // Set active notification type and load details
      this.notificationPreferencesStateService.setActiveNotificationType(
        alertType,
      );
      this.notificationPreferencesStateService.loadNotificationTypeDetails(
        alertType,
      );

      // Load "don't show again" preferences from session storage
      const dontShowAgainAllOn =
        sessionStorage.getItem(
          'UPCDontShowAgainToggleAllOnToast_' + alertType,
        ) === 'true';
      const dontShowAgainAllOff =
        sessionStorage.getItem(
          'UPCDontShowAgainToggleAllOffToast_' + alertType,
        ) === 'true';

      this.updateUIState({
        dontShowAgainAllOn,
        dontShowAgainAllOff,
      });
    });
  }

  /**
   * Lifecycle hook called when the page is about to leave
   * Updates the page in view state
   */
  ionViewWillLeave() {
    this.pageInViewSubject.next(false);
  }

  /**
   * Saves notification preferences for a specific group
   * @param group The notification group to save preferences for
   * @param toggleAllValue Optional flag indicating if all notifications were toggled
   */
  saveNotificationPreferences({
    group,
    toggleAllValue,
  }: {
    group: NotificationGroup;
    toggleAllValue?: boolean;
  }): void {
    // Prevent saving if there are no enabled customers, no toggle all value, and there are enabled delivery methods
    if (
      !group.enabledCustomers.length &&
      toggleAllValue === undefined &&
      group.enabledDeliveryMethods.length > 0
    ) {
      return;
    }

    // Handle "all on" toggle
    if (toggleAllValue && toggleAllValue === true) {
      if (!this.uiStateSubject.value.dontShowAgainAllOn) {
        this.updateUIState({
          toggleAllToastMessage: 'i18n.notifications.preferences.allOnMessage',
          showToggleAllToast: true,
        });
      }
    }
    // Handle "all off" toggle
    else if (toggleAllValue === false) {
      if (!this.uiStateSubject.value.dontShowAgainAllOff) {
        this.updateUIState({
          toggleAllToastMessage: 'i18n.notifications.preferences.allOffMessage',
          showToggleAllToast: true,
        });
      }
    }

    // Use firstValueFrom to get the latest state
    firstValueFrom(this.viewModel$)
      .then(viewModel => {
        if (!viewModel) {
          return;
        }

        const request: SaveNotificationPreferencesRequest = {
          userId: viewModel.user?.ecomUserId?.toString(),
          email: viewModel.user?.email ?? '',
          alerts: [
            {
              alertType: viewModel.notificationType?.type as AlertType,
              alerts: [
                {
                  alertName: group?.name,
                  notificationOptions: [
                    {
                      email: group?.enabledDeliveryMethods.includes('EMAIL'),
                      push: group?.enabledDeliveryMethods.includes('PUSH'),
                    },
                  ],
                  customers: group?.enabledCustomers.map(customer => ({
                    customerNumber: customer?.customerNumber,
                    divisionNumber: customer?.divisionNumber,
                  })),
                  optedInCustomers: group?.enabledCustomers.length,
                  alertEnabled: group?.enabledCustomers.length > 0,
                },
              ],
            },
          ],
        };

        // Save preferences
        this.pendingNotificationPreferencesService.clearPendingPreference(
          group.id,
        );
        this.notificationPreferencesStateService.saveNotificationPreferences(
          request,
        );
      })
      .catch(() => {
        this.toastService.presentToastMsg(
          this.translateService.instant('i18n.lists.genericError'),
          'red-toast',
          MessageTypeEnum.error,
          [],
        );
      });
  }

  /**
   * Handles the manage customers action for a notification group
   * @param alertGroup The notification group to manage customers for
   */
  manageCustomers(alertType: string, alertGroup: NotificationGroup): void {
    this.router.navigate([
      'notifications',
      PATHS.NOTIFICATION_PREFERENCES,
      'manage-customers',
      alertType,
      alertGroup.id,
    ]);
  }

  /**
   * Handles changes to the "don't show again" preference
   * @param checked Whether the "don't show again" option is checked
   * @param toastMessage The message type to handle
   */
  onDontShowAgainChange(checked: boolean, toastMessage: string) {
    if (!checked) {
      this.updateUIState({ showToggleAllToast: false });
      return;
    }

    this.viewModel$.pipe(take(1)).subscribe(vm => {
      const alertType = vm.notificationType?.type ?? '';

      // Handle "all off" don't show again preference
      if (toastMessage === 'i18n.notifications.preferences.allOffMessage') {
        this.updateUIState({
          dontShowAgainAllOff: checked,
          showToggleAllToast: false,
        });
        sessionStorage.setItem(
          'UPCDontShowAgainToggleAllOffToast_' + alertType,
          checked ? 'true' : 'false',
        );
        this.toastService.presentToastMsg(
          this.translateService.instant(
            'i18n.notifications.preferences.allOffDoNotShowAgain',
          ),
          'green-toast',
          MessageTypeEnum.success,
          [],
        );
      }
      // Handle "all on" don't show again preference
      else {
        this.updateUIState({
          dontShowAgainAllOn: checked,
          showToggleAllToast: false,
        });
        sessionStorage.setItem(
          'UPCDontShowAgainToggleAllOnToast_' + alertType,
          checked ? 'true' : 'false',
        );
        this.toastService.presentToastMsg(
          this.translateService.instant(
            'i18n.notifications.preferences.allOnDoNotShowAgain',
          ),
          'green-toast',
          MessageTypeEnum.success,
          [],
        );
      }
    });
  }

  /**
   * Updates the UI state with partial state changes
   * @param partialState Partial state object containing the changes to apply
   */
  private updateUIState(partialState: Partial<UIState>): void {
    this.uiStateSubject.next({
      ...this.uiStateSubject.value,
      ...partialState,
    });
  }
}
